import VideoWidget from './widgets/VideoWidget';
import SensorChartWidget from './widgets/SensorChartWidget';
import StatsWidget from './widgets/StatsWidget';
import RulesPickerWidget from './widgets/RulesPickerWidget';
import ComparisonWidget from './widgets/ComparisonWidget';
import StatTestingWidget from './widgets/StatTestingWidget';
import DistributionWidget from './widgets/DistributionWidget';
import ExportWidget from './widgets/ExportWidget';
import CorrelationWidget from './widgets/CorrelationWidget';
import AnomalyWidget from './widgets/AnomalyWidget';
import GPSMapWidget from './widgets/GPSMapWidget';
import NotesWidget from './widgets/NotesWidget';
import RateOfChangeWidget from './widgets/RateOfChangeWidget';
import MultiOverlayWidget from './widgets/MultiOverlayWidget';
import ExceedanceWidget from './widgets/ExceedanceWidget';
import AQIWidget from './widgets/AQIWidget';
import TimelineSensorChartWidget from './widgets/TimelineSensorChartWidget';
import UnifiedVideoWidget from './widgets/UnifiedVideoWidget';
import { sensorKeyToPollutant } from './statistics';

// Settings field types:
//   recording  — recording selector dropdown (key stores recording id)
//   sensor     — sensor key selector, filtered by dependsOn recording (key stores sensor key string)
//   sensorType — sensor type selector across all recordings (key stores type string)
//   toggle     — toggle button group (options: [{ value, label }])
//   slider     — numeric slider (min, max, step, default)

// --- canUse helpers ---
// Keys ending with latitude/longitude/altitude are GPS, not sensor data.
const GPS_SUFFIXES = ['latitude', 'longitude', 'altitude'];

function isGpsKey(key) {
  const lower = key.toLowerCase();
  return GPS_SUFFIXES.some((s) => lower.endsWith(s));
}

/** Return sensor-data keys (excludes GPS) across all recordings. */
export function getSensorKeys(recordings) {
  const keys = new Set();
  (recordings || []).forEach((rec) => {
    Object.keys(rec.data || {}).forEach((k) => {
      if (!isGpsKey(k)) keys.add(k);
    });
  });
  return keys;
}

/** True when at least one recording has non-GPS sensor data. */
export function hasAnySensorData(recordings) {
  return getSensorKeys(recordings).size > 0;
}

/** True when at least one recording contains video (not sensorsOnly). */
export function hasVideoData(recordings) {
  return (recordings || []).some((r) => !r.sensorsOnly);
}

/** True when at least one recording has GPS (latitude + longitude). */
export function hasGpsData(recordings) {
  return (recordings || []).some((rec) => {
    const keys = Object.keys(rec.data || {});
    return keys.some((k) => k.toLowerCase().endsWith('longitude'))
        && keys.some((k) => k.toLowerCase().endsWith('latitude'));
  });
}

/** True when at least one recording has AQI-compatible sensors. */
export function hasAqiSensors(recordings) {
  return (recordings || []).some((rec) =>
    Object.keys(rec.data || {}).some((k) => sensorKeyToPollutant(k) !== null)
  );
}

/** True when at least one recording has >= minCount non-GPS sensor keys. */
export function hasMinSensorKeys(recordings, minCount) {
  return (recordings || []).some((rec) => {
    const sensorCount = Object.keys(rec.data || {}).filter((k) => !isGpsKey(k)).length;
    return sensorCount >= minCount;
  });
}

// Reusable canUse predicates
const alwaysAvailable = () => true;
const needsSensorData = (recordings) => hasAnySensorData(recordings);
const needsVideo = (recordings) => hasVideoData(recordings);
const needsGps = (recordings) => hasGpsData(recordings);
const needsAqi = (recordings) => hasAqiSensors(recordings);
const needsTwoSensors = (recordings) => hasMinSensorKeys(recordings, 2);

const widgetRegistry = {
  video:          { component: VideoWidget,        label: 'Video Player',         icon: 'eva:video-fill',                       category: 'Charts & Media',       defaultSize: { w: 6, h: 4 }, requiresRecording: true,  requiresSensor: false, settings: [],                                                                                          canUse: needsVideo },
  unified_video:  { component: UnifiedVideoWidget, label: 'Unified Video Player', icon: 'eva:film-outline',                     category: 'Charts & Media',       defaultSize: { w: 6, h: 4 }, requiresRecording: false, requiresSensor: false, settings: [],                                                                                          canUse: needsVideo },
  sensor_chart:   { component: SensorChartWidget,  label: 'Sensor Chart',         icon: 'eva:activity-fill',                    category: 'Charts & Media',       defaultSize: { w: 6, h: 3 }, requiresRecording: true,  requiresSensor: true,  canUse: needsSensorData,
    settings: [
      { key: 'sensorKey', type: 'sensor', label: 'Sensor', dependsOn: 'recordingId' },
    ] },
  timeline_sensor_chart: { component: TimelineSensorChartWidget, label: 'Unified Sensor Chart', icon: 'eva:activity-outline', category: 'Charts & Media', defaultSize: { w: 6, h: 3 }, requiresRecording: false, requiresSensor: false, canUse: needsSensorData,
    settings: [
      { key: 'selectedSensorType', type: 'sensorType', label: 'Sensor Type' },
    ] },
  multi_overlay:  { component: MultiOverlayWidget, label: 'Dual-Axis Chart',      icon: 'eva:layers-fill',                      category: 'Charts & Media',       defaultSize: { w: 6, h: 3 }, requiresRecording: false, requiresSensor: false, canUse: needsSensorData,
    settings: [
      { key: 'recId', type: 'recording', label: 'Recording' },
      { key: 'sensorKey1', type: 'sensor', label: 'Primary Sensor', dependsOn: 'recId' },
      { key: 'sensorKey2', type: 'sensor', label: 'Secondary Sensor', dependsOn: 'recId', allowEmpty: true },
    ] },
  rate_of_change: { component: RateOfChangeWidget, label: 'Rate of Change',       icon: 'eva:trending-up-fill',                 category: 'Charts & Media',       defaultSize: { w: 6, h: 3 }, requiresRecording: false, requiresSensor: false, canUse: needsSensorData,
    settings: [
      { key: 'recId', type: 'recording', label: 'Recording' },
      { key: 'sensorKey', type: 'sensor', label: 'Sensor', dependsOn: 'recId' },
    ] },

  stats:          { component: StatsWidget,        label: 'Statistics',           icon: 'eva:bar-chart-fill',                   category: 'Statistics',           defaultSize: { w: 3, h: 3 }, requiresRecording: true,  requiresSensor: true,  canUse: needsSensorData,
    settings: [
      { key: 'sensorKey', type: 'sensor', label: 'Sensor', dependsOn: 'recordingId' },
    ] },
  comparison:     { component: ComparisonWidget,   label: 'Comparison',           icon: 'eva:swap-fill',                        category: 'Statistics',           defaultSize: { w: 6, h: 4 }, requiresRecording: false, requiresSensor: false, canUse: needsSensorData,
    settings: [
      { key: 'recId1', type: 'recording', label: 'Recording 1' },
      { key: 'recId2', type: 'recording', label: 'Recording 2' },
      { key: 'sensorKey', type: 'sensor', label: 'Sensor', dependsOn: 'recId1' },
    ] },
  stat_testing:   { component: StatTestingWidget,  label: 'Statistical Testing',  icon: 'eva:checkmark-circle-fill',            category: 'Statistics',           defaultSize: { w: 5, h: 3 }, requiresRecording: false, requiresSensor: false, canUse: needsSensorData,
    settings: [
      { key: 'recId1', type: 'recording', label: 'Recording 1' },
      { key: 'recId2', type: 'recording', label: 'Recording 2' },
      { key: 'sensorKey', type: 'sensor', label: 'Sensor', dependsOn: 'recId1' },
    ] },
  distribution:   { component: DistributionWidget, label: 'Distribution Shape',   icon: 'eva:bell-fill',                        category: 'Statistics',           defaultSize: { w: 5, h: 3 }, requiresRecording: false, requiresSensor: false, canUse: needsSensorData,
    settings: [
      { key: 'recId1', type: 'recording', label: 'Recording 1' },
      { key: 'recId2', type: 'recording', label: 'Recording 2' },
      { key: 'sensorKey', type: 'sensor', label: 'Sensor', dependsOn: 'recId1' },
    ] },
  correlation:    { component: CorrelationWidget,  label: 'Correlation',          icon: 'eva:diagonal-arrow-right-up-fill',     category: 'Statistics',           defaultSize: { w: 5, h: 4 }, requiresRecording: false, requiresSensor: false, canUse: needsTwoSensors,
    settings: [
      { key: 'recId', type: 'recording', label: 'Recording' },
      { key: 'sensorKeyX', type: 'sensor', label: 'Sensor X', dependsOn: 'recId' },
      { key: 'sensorKeyY', type: 'sensor', label: 'Sensor Y', dependsOn: 'recId' },
    ] },

  anomaly:        { component: AnomalyWidget,      label: 'Anomaly Detection',    icon: 'eva:alert-triangle-fill',              category: 'Detection & Quality',  defaultSize: { w: 4, h: 3 }, requiresRecording: false, requiresSensor: false, canUse: needsSensorData,
    settings: [
      { key: 'recId', type: 'recording', label: 'Recording' },
      { key: 'sensorKey', type: 'sensor', label: 'Sensor', dependsOn: 'recId' },
      { key: 'method', type: 'toggle', label: 'Method', options: [{ value: 'iqr', label: 'IQR Fences' }, { value: 'zscore', label: 'Z-Score (3σ)' }], default: 'iqr' },
    ] },
  aqi:            { component: AQIWidget,          label: 'AQI Calculator',       icon: 'eva:thermometer-fill',                 category: 'Detection & Quality',  defaultSize: { w: 3, h: 4 }, requiresRecording: false, requiresSensor: false, canUse: needsAqi,
    settings: [
      { key: 'recId', type: 'recording', label: 'Recording' },
      { key: 'sensorKey', type: 'sensor', label: 'Pollutant', dependsOn: 'recId', filterAQI: true },
    ] },
  exceedance:     { component: ExceedanceWidget,   label: 'Threshold Exceedance', icon: 'eva:clock-fill',                       category: 'Detection & Quality',  defaultSize: { w: 5, h: 3 }, requiresRecording: false, requiresSensor: false, canUse: needsSensorData,
    settings: [
      { key: 'recId', type: 'recording', label: 'Recording' },
    ] },
  rules:          { component: RulesPickerWidget,  label: 'Rules',                icon: 'eva:shield-fill',                      category: 'Detection & Quality',  defaultSize: { w: 3, h: 3 }, requiresRecording: false, requiresSensor: false, settings: [],                                                                                          canUse: needsSensorData },

  gps_map:        { component: GPSMapWidget,       label: 'GPS Map',              icon: 'eva:pin-fill',                         category: 'Spatial & Notes',      defaultSize: { w: 5, h: 4 }, requiresRecording: false, requiresSensor: false, canUse: needsGps,
    settings: [
      { key: 'recId', type: 'recording', label: 'Recording' },
      { key: 'sensorKey', type: 'sensor', label: 'Color by sensor', dependsOn: 'recId', allowEmpty: true },
    ] },
  notes:          { component: NotesWidget,        label: 'Notes',                icon: 'eva:edit-2-fill',                      category: 'Spatial & Notes',      defaultSize: { w: 3, h: 3 }, requiresRecording: false, requiresSensor: false, settings: [],                                                                                          canUse: alwaysAvailable },
  export:         { component: ExportWidget,       label: 'Export / Report',      icon: 'eva:download-fill',                    category: 'Spatial & Notes',      defaultSize: { w: 3, h: 2 }, requiresRecording: false, requiresSensor: false, settings: [],                                                                                          canUse: alwaysAvailable },
};

export const WIDGET_CATEGORIES = ['Charts & Media', 'Statistics', 'Detection & Quality', 'Spatial & Notes'];

export function getWidgetDef(type) {
  return widgetRegistry[type];
}

export function getAllWidgetTypes() {
  return Object.entries(widgetRegistry).map(([type, def]) => ({ type, ...def }));
}

export function getWidgetTypesByCategory() {
  const all = getAllWidgetTypes();
  return WIDGET_CATEGORIES.map((cat) => ({
    category: cat,
    widgets: all.filter((w) => w.category === cat),
  }));
}

export default widgetRegistry;
